import React from 'react';
import { Clock } from 'lucide-react';
import { CompteursPoste, EtatPoste, LIBELLE_ETAT, etatDuPoste } from './extrasEtatPoste';

/**
 * Le suivi des extras d'une privatisation, poste par poste.
 *
 * Emeni : « je trouve que l'affichage est compliqué, on ne comprend pas trop ». Le suivi affichait tous les
 * postes d'un dossier dans une seule cellule (« Hôtesse x2 · 18:00 → 02:00, Hôtesse x2 · 20:00 → 02:00,
 * Vestiaire x3 · 19:00 → 02:00 ») : on lisait un paragraphe au lieu de voir des chiffres. Et le statut
 * affiché était celui du poste le moins avancé, si bien qu'un dossier à moitié confirmé s'annonçait
 * « à demander ».
 *
 * Une ligne par poste, et surtout trois colonnes chiffrées SÉPARÉES — requis, interne, prestataire — parce
 * que c'est la seule question qu'on se pose devant cet écran : combien il m'en faut, combien j'en ai, et
 * d'où ils viennent.
 *
 * Ce composant sert à la fois la fiche de privatisation (mode compact) et le suivi global. Une seule
 * implémentation : deux écrans qui affichent la même chose doivent le calculer au même endroit.
 */

export interface PosteSuivi extends CompteursPoste {
    typeId: string;
    label: string;
    /** Créneaux demandés au prestataire, chacun avec sa quantité. Un poste peut être découpé. */
    slots?: { start: string; end: string; quantity: number }[];
    /** Salariés de la maison bookés sur ce poste. */
    internes?: string[];
}

const TON_ETAT: Record<EtatPoste, string> = {
    a_booker_interne: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    a_commander: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    reponse_attendue: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    complet: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
};

const Pastille = ({ etat }: { etat: EtatPoste }) => (
    <span className={`inline-block whitespace-nowrap text-[10px] font-bold px-2 py-0.5 rounded-lg border ${TON_ETAT[etat]}`}>
        {LIBELLE_ETAT[etat]}
    </span>
);

export function SuiviExtrasParPoste({ postes }: { postes: PosteSuivi[] }) {
    if (!postes.length) return null;
    return (
        <table className="w-full table-fixed border-collapse text-xs">
            <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                    <th className="w-[38%] text-left font-bold pb-1.5">Poste</th>
                    <th className="w-[12%] text-center font-bold pb-1.5">Requis</th>
                    <th className="w-[12%] text-center font-bold pb-1.5">Interne</th>
                    <th className="w-[14%] text-center font-bold pb-1.5">Prestataire</th>
                    <th className="w-[24%] text-right font-bold pb-1.5">État</th>
                </tr>
            </thead>
            <tbody>
                {postes.map((p) => {
                    const { etat } = etatDuPoste(p);
                    return (
                        <tr key={p.typeId} className="border-t border-[var(--border-color)] align-top">
                            <td className="py-2 pr-2">
                                <div className="font-bold text-[var(--text-primary)] break-words">{p.label}</div>
                                {/* Les horaires descendent en seconde ligne : ils comptent, mais ce n'est pas
                                    ce qu'on cherche en premier. Chaque créneau garde le sien — les résumer en
                                    une seule plage afficherait une heure fausse. */}
                                {(p.slots || []).length > 0 && (
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-[var(--text-muted)]">
                                        {(p.slots || []).map((s, i) => (
                                            <span key={i} className="flex items-center gap-1 whitespace-nowrap">
                                                <Clock size={10} /> {s.quantity} x {s.start}{s.end ? ` → ${s.end}` : ''}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {(p.internes || []).length > 0 && (
                                    <div className="mt-0.5 text-[10px] text-[var(--text-muted)] break-words">
                                        {(p.internes || []).join(', ')}
                                    </div>
                                )}
                            </td>
                            <td className="py-2 text-center text-[var(--text-primary)] font-bold">{p.requis}</td>
                            <td className="py-2 text-center text-[var(--text-primary)]">{p.interne || <span className="text-[var(--text-muted)]">0</span>}</td>
                            <td className="py-2 text-center text-[var(--text-primary)]">{p.commande || <span className="text-[var(--text-muted)]">—</span>}</td>
                            <td className="py-2 text-right"><Pastille etat={etat} /></td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
